// @flow
import * as React from 'react'
import PopperJS from 'popper.js'

// Types
type Props = {
  wrapper: any,
  target: Function,
  popper: Function,
  arrow?: Function,
  placement?: *,
  modifiers?: Object,
  showed?: boolean,
  onHidePopper?: Function,
  onShowPopper?: Function,
}
type State = {
  data: ?Object,
  showed: boolean,
}

/**
 * A tiny wrapper around Popper.js (https://popper.js.org), which
 * can be used for tooltips, dropdown, etc. It is more powerfull than
 * existing `react-popper` wrapper and have simpler interface (one component
 * instead four).
 *
 * @param {any} props.wrapper       Valid react element or component (string, func, class),
 *                                  that will be used as a wrapper around target. Needed
 *                                  because React 15 do not support array as return from render.
 *                                  Default: 'div'
 * @param {Function} props.target   Render function that returns an element which will be tracked
 *                                  as "reference" in terms of Popper.js. Accepts three arguments:
 *                                  innerRef to set in `ref` of the element to get DOM element of
 *                                  target (required), targetProps object which contains some event
 *                                  handlers to track mouse hover/click/focus (optional), data object
 *                                  from popper which can be used to get offsets of different popper
 *                                  elements, like reference width (optional)
 * @param {Function} props.popper   Render function that returns an element of popper (tooltip for
 *                                  example). Accepts the same arguments as `props.target`
 * @param {Function} props.arrow    Render function that returns an element for arrow (for tooltip
 *                                  for example). Accepts the same arguments as `props.target`
 * @param {string} props.placement  String value of placement. See popper.js docs for values.
 * @param {bool} props.showed       If passed true/false then the component won't track any events
 *                                  on target element to control visibility of the popper. So, this
 *                                  prop manually control the visibility of the popper element.
 */
class Popper extends React.Component<Props, State> {
  state = { data: null, showed: false }

  isTouch = false

  unmounted = false

  popperInstance = null

  targetNode = null

  popperNode = null

  arrowNode = null

  hideTimer: any

  static defaultProps = {
    wrapper: 'div',
    placement: 'top',
  }

  componentDidUpdate(lastProps: Props) {
    if (lastProps.placement !== this.props.placement) {
      this.maybeCreatePopper()
    }
    if (lastProps.showed !== this.props.showed) {
      this.scheduleUpdate()
    }
  }

  componentDidMount() {
    document.addEventListener('touchend', this.handleClickOutside, true)
    document.addEventListener('click', this.handleClickOutside, true)
    document.addEventListener('focusin', this.handleClickOutside, false)
  }

  componentWillUnmount() {
    this.unmounted = true
    this.destroyPopperInstance()
    document.removeEventListener('touchend', this.handleClickOutside, true)
    document.removeEventListener('click', this.handleClickOutside, true)
    document.removeEventListener('focusin', this.handleClickOutside, false)
  }

  setTargetRef = (targetNode: HTMLElement) => {
    this.targetNode = targetNode
    this.maybeCreatePopper()
  }

  setPopperRef = (popperNode: HTMLElement) => {
    this.popperNode = popperNode
    this.maybeCreatePopper()
  }

  setArrowRef = (arrowNode: HTMLElement) => {
    this.arrowNode = arrowNode
    this.maybeCreatePopper()
  }

  handlePopperDirectEvent = () => {
    clearTimeout(this.hideTimer)
  }

  handlePopperOppositeEvent = () => {
    this.scheduleHidePopper()
  }

  handleTargetDirectEvent = () => {
    this.showPopper()
  }

  handleTargetOppositeEvent = () => {
    if (this.isPopperShowed()) {
      this.scheduleHidePopper()
    }
  }

  handleClickOutside = (e: any) => {
    if (e.type === 'touchend') this.isTouch = true
    if (e.type === 'click' && this.isTouch) return
    if (
      this.targetNode &&
      !this.targetNode.contains(e.target) &&
      this.popperNode &&
      !this.popperNode.contains(e.target)
    ) {
      this.immidiateHidePopper()
    }
  }

  scheduleHidePopper = () => {
    this.hideTimer = setTimeout(this.immidiateHidePopper, 100)
  }

  immidiateHidePopper = () => {
    clearTimeout(this.hideTimer)
    if (!this.unmounted) {
      this.setState({ showed: false })
      const { onHidePopper } = this.props
      if (onHidePopper) onHidePopper()
    }
  }

  showPopper() {
    clearTimeout(this.hideTimer)
    if (!this.isPopperShowed()) {
      this.setState({ showed: true }, () => {
        this.scheduleUpdate()
      })
      const { onShowPopper } = this.props
      if (onShowPopper) onShowPopper()
    }
  }

  handleClick = () => {
    this.handleTargetDirectEvent()
    this.handleTargetOppositeEvent()
  }

  handlePopperUpdate = (data: Object) => {
    if (!this.unmounted) {
      this.setState({ data })
    }
  }

  maybeCreatePopper() {
    if (
      this.targetNode &&
      this.popperNode &&
      (!this.props.arrow || this.arrowNode)
    ) {
      this.createPopperInstance()
    }
  }

  createPopperInstance() {
    const { arrow, placement, modifiers } = this.props

    this.destroyPopperInstance()
    const popperOptions = {
      onCreate: this.handlePopperUpdate,
      onUpdate: this.handlePopperUpdate,
      placement,
      modifiers: {
        applyStyle: false,
        arrow: arrow ? { element: this.arrowNode } : undefined,
        ...modifiers,
      },
    }
    if (this.targetNode && this.popperNode) {
      this.popperInstance = new PopperJS(
        this.targetNode,
        this.popperNode,
        popperOptions
      )
    }
    setTimeout(() => this.scheduleUpdate())
  }

  destroyPopperInstance() {
    if (this.popperInstance) {
      this.popperInstance.destroy()
      this.popperInstance = null
    }
  }

  scheduleUpdate = () => {
    if (this.popperInstance) {
      this.popperInstance.scheduleUpdate()
    }
  }

  getPopperStyles() {
    const { data } = this.state

    if (
      !data ||
      !this.popperInstance ||
      data.offsets.popper.left !== data.offsets.popper.left // eslint-disable-line no-self-compare
    ) {
      return {
        position: 'absolute',
        pointerEvents: 'none',
        opacity: 0,
      }
    }

    return {
      position: data.offsets.popper.position,
      ...data.styles,
    }
  }

  getArrowStyles() {
    const { data } = this.state
    if (
      !data ||
      !data.offsets.arrow ||
      data.offsets.arrow.left !== data.offsets.arrow.left // eslint-disable-line no-self-compare
    ) {
      return {}
    }
    const { top, left } = data.offsets.arrow
    return { top, left }
  }

  isPopperShowed() {
    if (typeof this.props.showed === 'undefined') {
      return this.state.showed
    }
    return this.props.showed
  }

  getPopperData() {
    const data = this.state.data || {}
    const placement = data.placement || ''
    return {
      placement,
      hidePopper: this.immidiateHidePopper,
      roughPlacement: placement.split('-')[0],
      showed: this.isPopperShowed(),
      offsets: data.offsets || {
        arrow: {},
        popper: {},
        reference: {},
      },
    }
  }

  render() {
    const { target, popper, arrow, wrapper } = this.props
    const data = this.getPopperData()

    const targetElem = target(
      this.setTargetRef,
      {
        onMouseEnter: this.handleTargetDirectEvent,
        onMouseLeave: this.handleTargetOppositeEvent,
        onFocus: this.handleTargetDirectEvent,
        onBlur: this.handleTargetOppositeEvent,
        onClick: this.handleClick,
      },
      data
    )
    const arrowElem =
      arrow && arrow(this.setArrowRef, { style: this.getArrowStyles() }, data)
    const popperElem = popper(
      this.setPopperRef,
      {
        style: this.getPopperStyles(),
        onMouseEnter: this.handlePopperDirectEvent,
        onMouseLeave: this.handlePopperOppositeEvent,
        children: arrowElem,
      },
      data
    )

    const createFunc = React.isValidElement(wrapper)
      ? React.cloneElement
      : React.createElement

    return createFunc(wrapper, null, targetElem, popperElem)
  }
}

export default Popper
